import type {
  InternalNetworkAdEntityKindSchema,
  SignalCategoryType,
} from '@terra/shared-schemas-orchestrator';

export const INTERNAL_NETWORK_SCOPE_SECTION = {
  domainNames: 'domainNames',
  dcIps: 'dcIps',
  dnsServerIps: 'dnsServerIps',
  signalCategories: 'signalCategories',
  attackPathScope: 'attackPathScope',
} as const;

export type InternalNetworkScopeSection =
  (typeof INTERNAL_NETWORK_SCOPE_SECTION)[keyof typeof INTERNAL_NETWORK_SCOPE_SECTION];

export const INTERNAL_NETWORK_SCOPE_LIST_SECTIONS = [
  INTERNAL_NETWORK_SCOPE_SECTION.domainNames,
  INTERNAL_NETWORK_SCOPE_SECTION.dcIps,
  INTERNAL_NETWORK_SCOPE_SECTION.dnsServerIps,
] as const satisfies readonly InternalNetworkScopeSection[];

export type InternalNetworkScopeListSection =
  (typeof INTERNAL_NETWORK_SCOPE_LIST_SECTIONS)[number];

export type InternalNetworkScopeLists = Record<
  InternalNetworkScopeListSection,
  string[]
>;

export type InternalNetworkScopeStored = InternalNetworkScopeLists & {
  signalCategoriesInScope: SignalCategoryType[];
};

export type InternalNetworkScopePatch =
  | InternalNetworkScopeLists
  | { signalCategoriesInScope: SignalCategoryType[] };

export type InternalNetworkAdEntityKind =
  (typeof InternalNetworkAdEntityKindSchema.options)[number];

export type InternalNetworkAttackPathEntity = {
  id: string;
  entityId: string;
  entityKind: InternalNetworkAdEntityKind;
  label: string | null;
};

const INTERNAL_NETWORK_SCOPE_SECTION_VALUES: readonly string[] = Object.values(
  INTERNAL_NETWORK_SCOPE_SECTION,
);

const INTERNAL_NETWORK_SCOPE_LIST_SECTION_VALUES: readonly string[] =
  INTERNAL_NETWORK_SCOPE_LIST_SECTIONS;

export const isInternalNetworkScopeSection = (
  value: string,
): value is InternalNetworkScopeSection =>
  INTERNAL_NETWORK_SCOPE_SECTION_VALUES.includes(value);

export const isInternalNetworkScopeListSection = (
  value: string,
): value is InternalNetworkScopeListSection =>
  INTERNAL_NETWORK_SCOPE_LIST_SECTION_VALUES.includes(value);

export const areStringListsEqual = ({
  left,
  right,
}: {
  left: string[];
  right: string[];
}): boolean =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

export const pickInternalNetworkScopeLists = (
  stored: InternalNetworkScopeLists,
): InternalNetworkScopeLists => ({
  domainNames: stored.domainNames,
  dcIps: stored.dcIps,
  dnsServerIps: stored.dnsServerIps,
});

export const isInternalNetworkScopeListsDirty = ({
  draft,
  stored,
}: {
  draft: InternalNetworkScopeLists;
  stored: InternalNetworkScopeLists;
}): boolean =>
  INTERNAL_NETWORK_SCOPE_LIST_SECTIONS.some(
    (section) =>
      !areStringListsEqual({ left: draft[section], right: stored[section] }),
  );

export const shouldAutoSaveInternalNetworkScopeLists = ({
  draft,
  stored,
  isLocked,
  isSaving,
}: {
  draft: InternalNetworkScopeLists;
  stored: InternalNetworkScopeLists;
  isLocked: boolean;
  isSaving: boolean;
}): boolean =>
  !isLocked &&
  !isSaving &&
  draft.domainNames.length > 0 &&
  isInternalNetworkScopeListsDirty({ draft, stored });

export const isInternalNetworkScopeLocked = ({
  isReadOnly,
  isDiscoveryActive,
}: {
  isReadOnly: boolean;
  isDiscoveryActive: boolean;
}): boolean => isReadOnly || isDiscoveryActive;

export const resolveAttackPathEntityName = ({
  label,
  entityId,
}: Pick<InternalNetworkAttackPathEntity, 'label' | 'entityId'>): string =>
  label ?? entityId;
